import { z } from 'zod';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { listEngagementDocuments } from './documents';

/**
 * Engagement requirements surface (T-0142 / E11-F04) and the library load
 * (T-0143 / E11-F05). Backs Engagement -> Selection -> Requirements.
 *
 * Contract: `apps/api/src/routes/engagement-requirements.ts` and
 * `apps/api/src/services/engagement-requirements.ts` (T-0138, T-0139).
 * Everything is under `/api/v1/engagements/:engagement_id/requirements`.
 *
 *   - The whole engagement comes back in one response (no pagination); the
 *     table filters, counts and virtualises client-side.
 *   - No request carries a number or a citation other than a manual
 *     reason: the server assigns numbers (amendment 01, Decision 32) and
 *     refuses unknown fields.
 *   - `not_found` covers an unknown engagement, a non-member, and an
 *     engagement whose Selection module is off.
 *   - `:load` needs an `Idempotency-Key`; the caller supplies one per
 *     confirmed load so a retry of the same confirm happens once.
 */

export const CRITICALITIES = [
  'not_set',
  'critical',
  'desired',
  'not_required',
  'not_applicable',
] as const;
export type Criticality = (typeof CRITICALITIES)[number];

const RequirementLoadSchema = z.object({
  id: z.string(),
  kind: z.string(),
  reason: z.string().nullable(),
  created_by: z.string(),
  created_by_name: z.string().nullable(),
  created_at: z.string(),
});
export type RequirementLoad = z.infer<typeof RequirementLoadSchema>;

/**
 * One item of discovery evidence a requirement cites (E12-F10 §3): its
 * words, who it is from, where and when it was said. A withdrawn item
 * stays cited (Decision 54.20). Everything after `added_at` may be absent
 * from an older server; the kinds stay open strings so an unexpected one
 * never breaks the list.
 */
export const RequirementEvidenceSchema = z.object({
  evidence_id: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  suggestion_id: z.string().nullable(),
  added_at: z.string(),
  text: z.string().optional(),
  /** interview, questionnaire, follow_up, document or data. */
  channel: z.string().nullish(),
  source: z.object({ kind: z.string(), id: z.string(), name: z.string() }).nullish(),
  citation: z.object({ kind: z.string(), label: z.string(), location: z.string() }).nullish(),
  interview: z
    .object({
      id: z.string(),
      code: z.string(),
      title: z.string(),
      held_at: z.string().nullable(),
    })
    .nullish(),
  document: z.object({ id: z.string(), title: z.string(), page: z.number().nullable() }).nullish(),
  /** The interview's date once held, else the day the item was confirmed. */
  date: z.string().nullish(),
  withdraw_reason: z.string().nullish(),
  withdrawn_at: z.string().nullish(),
});
export type RequirementEvidence = z.infer<typeof RequirementEvidenceSchema>;

export const EngagementRequirementSchema = z.object({
  id: z.string(),
  engagement_id: z.string(),
  number: z.string(),
  /** Null for a requirement not mapped to a library module ("Not mapped"). */
  module_id: z.string().nullable(),
  module_name: z.string().nullable(),
  /** The client's own functional area, as its spreadsheet wrote it; absent from an older server. */
  client_area: z.string().nullable().optional(),
  subcategory: z.string(),
  text: z.string(),
  criticality: z.string(),
  status: z.string(),
  is_edited: z.boolean(),
  library_item_id: z.string().nullable(),
  library_module_version: z.number().int().nullable(),
  /** Shape depends on `kind`; read it with `citationOf` in the screen logic. */
  citation: z.unknown(),
  load_id: z.string().nullable(),
  load: RequirementLoadSchema.nullable(),
  /** Optional: a server from before the field existed sends none. */
  discovery_evidence: z.array(RequirementEvidenceSchema).optional(),
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type EngagementRequirement = z.infer<typeof EngagementRequirementSchema>;

export const EngagementRequirementListSchema = z.object({
  items: z.array(EngagementRequirementSchema),
  total: z.number().int(),
});
export type EngagementRequirementList = z.infer<typeof EngagementRequirementListSchema>;

export const BulkResultSchema = z.object({
  updated: z.number().int(),
  ids: z.array(z.string()),
  unchanged: z.number().int(),
  /** The prior value of each changed row, which is what an undo restores. */
  previous: z.array(
    z.object({
      id: z.string(),
      number: z.string(),
      criticality: z.string(),
      status: z.string(),
    }),
  ),
});
export type BulkResult = z.infer<typeof BulkResultSchema>;

export const LoadPreviewSchema = z.object({
  to_add: z.array(z.string()),
  already_present: z.array(z.string()),
  removed_earlier: z.array(z.string()),
  unknown_or_retired: z.array(z.string()),
  counts: z.object({
    to_add: z.number().int(),
    already_present: z.number().int(),
    removed_earlier: z.number().int(),
    unknown_or_retired: z.number().int(),
  }),
});
export type LoadPreview = z.infer<typeof LoadPreviewSchema>;

export const LoadResultSchema = z.object({
  load_id: z.string(),
  added: z.number().int(),
  restored: z.number().int(),
  skipped: z.number().int(),
  skipped_detail: z.object({
    already_present: z.number().int(),
    removed_earlier: z.number().int(),
    unknown_or_retired: z.number().int(),
  }),
});
export type LoadResult = z.infer<typeof LoadResultSchema>;

export const ACTIVITY_KINDS = [
  'created',
  'loaded',
  'reworded',
  'criticality_changed',
  'subcategory_changed',
  'module_changed',
  'client_area_changed',
  'removed',
  'restored',
] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

/** One entry of a requirement's history, read from the audit trail (T-0152). */
export const RequirementActivityEntrySchema = z.object({
  id: z.string(),
  /** One of {@link ACTIVITY_KINDS}; an unknown kind is shown generically. */
  kind: z.string(),
  at: z.string(),
  /** Null for a system action. */
  actor: z.object({ id: z.string(), name: z.string().nullable() }).nullable(),
  /** Set when the change was one bulk action over several requirements. */
  bulk: z.object({ count: z.number().int() }).nullable(),
  from: z.string().nullable(),
  to: z.string().nullable(),
  reason: z.string().nullable(),
  load: z.object({ id: z.string(), kind: z.string() }).nullable(),
});
export type RequirementActivityEntry = z.infer<typeof RequirementActivityEntrySchema>;

export const RequirementActivitySchema = z.object({
  requirement_id: z.string(),
  number: z.string(),
  entries: z.array(RequirementActivityEntrySchema),
});
export type RequirementActivity = z.infer<typeof RequirementActivitySchema>;

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

function base(engagementId: string): string {
  return `/api/v1/engagements/${encodeURIComponent(engagementId)}/requirements`;
}

export function newIdempotencyKey(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Every requirement of the engagement, active and removed. */
export function listEngagementRequirements(
  engagementId: string,
): Promise<EngagementRequirementList> {
  return apiClient(base(engagementId), EngagementRequirementListSchema, {
    query: { status: 'all' },
  });
}

/** One requirement's history, newest first. */
export function getRequirementActivity(
  engagementId: string,
  requirementId: string,
): Promise<RequirementActivity> {
  return apiClient(
    `${base(engagementId)}/${encodeURIComponent(requirementId)}/activity`,
    RequirementActivitySchema,
  );
}

export interface CreateManualRequirementInput {
  module_id: string;
  subcategory: string;
  text: string;
  criticality: Criticality;
  /** Why the requirement is being added: its citation (Decision 34). */
  reason: string;
  supporting_document_id?: string;
  /**
   * Add it although the engagement already has a requirement with the same
   * wording. Without it the server refuses an exact duplicate with a 409
   * (`details.reason: "duplicate_text"`).
   */
  acknowledge_duplicate?: boolean;
}

export function createManualRequirement(
  engagementId: string,
  input: CreateManualRequirementInput,
): Promise<EngagementRequirement> {
  return apiClient(base(engagementId), EngagementRequirementSchema, {
    method: 'POST',
    body: {
      module_id: input.module_id,
      subcategory: input.subcategory,
      text: input.text,
      criticality: input.criticality,
      citation: {
        kind: 'manual',
        reason: input.reason,
        ...(input.supporting_document_id
          ? { supporting_document_id: input.supporting_document_id }
          : {}),
      },
      ...(input.acknowledge_duplicate ? { acknowledge_duplicate: true } : {}),
    },
    idempotencyKey: newIdempotencyKey(),
  });
}

export interface UpdateRequirementInput {
  id: string;
  text?: string;
  criticality?: Criticality;
  subcategory?: string;
  /** The client area; null or blank clears it. */
  client_area?: string | null;
  /** A library module id, or null for Not mapped (a requirement with no library item only). */
  module_id?: string | null;
}

export function updateRequirement(
  engagementId: string,
  input: UpdateRequirementInput,
): Promise<EngagementRequirement> {
  const body: Record<string, unknown> = {};
  if (input.text !== undefined) body['text'] = input.text;
  if (input.criticality !== undefined) body['criticality'] = input.criticality;
  if (input.subcategory !== undefined) body['subcategory'] = input.subcategory;
  if (input.client_area !== undefined) body['client_area'] = input.client_area;
  if (input.module_id !== undefined) body['module_id'] = input.module_id;
  return apiClient(
    `${base(engagementId)}/${encodeURIComponent(input.id)}`,
    EngagementRequirementSchema,
    { method: 'PATCH', body },
  );
}

export function setRequirementStatus(
  engagementId: string,
  id: string,
  action: 'remove' | 'restore',
): Promise<EngagementRequirement> {
  return apiClient(
    `${base(engagementId)}/${encodeURIComponent(id)}:${action}`,
    EngagementRequirementSchema,
    { method: 'POST' },
  );
}

export function bulkUpdateRequirements(
  engagementId: string,
  ids: string[],
  criticality: Criticality,
): Promise<BulkResult> {
  return apiClient(`${base(engagementId)}:bulk-update`, BulkResultSchema, {
    method: 'POST',
    body: { ids, criticality },
  });
}

export function bulkSetRequirementStatus(
  engagementId: string,
  action: 'remove' | 'restore',
  ids: string[],
): Promise<BulkResult> {
  return apiClient(`${base(engagementId)}:bulk-${action}`, BulkResultSchema, {
    method: 'POST',
    body: { ids },
  });
}

export function previewLibraryLoad(
  engagementId: string,
  itemIds: string[],
  signal?: AbortSignal,
): Promise<LoadPreview> {
  return apiClient(`${base(engagementId)}:load-preview`, LoadPreviewSchema, {
    method: 'POST',
    body: { item_ids: itemIds },
    signal,
  });
}

export interface LoadLibraryInput {
  item_ids: string[];
  restore_removed: boolean;
  reason: string;
  /** One key per confirmed load; a retry must reuse it. */
  idempotencyKey: string;
}

export function loadLibraryRequirements(
  engagementId: string,
  input: LoadLibraryInput,
): Promise<LoadResult> {
  return apiClient(`${base(engagementId)}:load`, LoadResultSchema, {
    method: 'POST',
    body: {
      item_ids: input.item_ids,
      restore_removed: input.restore_removed,
      reason: input.reason,
    },
    idempotencyKey: input.idempotencyKey,
  });
}

// ---------------------------------------------------------------------------
// Query + mutation hooks
// ---------------------------------------------------------------------------

export const engagementRequirementKeys = {
  all: (engagementId: string) => ['engagement-requirements', engagementId] as const,
  list: (engagementId: string) => ['engagement-requirements', engagementId, 'list'] as const,
  activity: (engagementId: string, requirementId: string) =>
    ['engagement-requirements', engagementId, 'activity', requirementId] as const,
};

/**
 * The history of one requirement, for its side panel.
 *
 * The server builds it from the audit trail, which is written a moment
 * after the change it records. `revision` (the row's `updated_at`) is part
 * of the cache key, so an edit refetches; the previous list stays on
 * screen meanwhile. The panel's `RequirementActivity` asks once more
 * shortly after, for the entry that had not been written yet.
 */
export function useRequirementActivityQuery(
  engagementId: string,
  requirementId: string,
  revision: string,
) {
  return useQuery({
    queryKey: [...engagementRequirementKeys.activity(engagementId, requirementId), revision],
    queryFn: () => getRequirementActivity(engagementId, requirementId),
    staleTime: 0,
    retry: false,
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[3] === requirementId ? prev : undefined,
  });
}

/** `enabled` false skips the read (the engagement Overview, when Selection is off). */
export function useEngagementRequirementsQuery(engagementId: string, enabled = true) {
  return useQuery({
    queryKey: engagementRequirementKeys.list(engagementId),
    queryFn: () => listEngagementRequirements(engagementId),
    enabled,
    staleTime: 15_000,
    // A module that is off answers not_found: retrying will not change it.
    retry: false,
  });
}

/**
 * The engagement's documents, for naming or choosing a manual
 * requirement's supporting document. Fetched on demand, without the
 * polling the Documents screen does.
 */
export function useSupportingDocumentsQuery(engagementId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...engagementRequirementKeys.all(engagementId), 'documents'] as const,
    queryFn: () => listEngagementDocuments(engagementId),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

type RowPatch = Partial<
  Pick<EngagementRequirement, 'text' | 'criticality' | 'status' | 'client_area'>
> & {
  is_edited?: boolean;
};

/** Apply a patch to cached rows straight away; returns the cache as it was. */
function patchCachedRows(
  qc: QueryClient,
  engagementId: string,
  ids: ReadonlyArray<string>,
  patch: RowPatch,
): EngagementRequirementList | undefined {
  const key = engagementRequirementKeys.list(engagementId);
  const before = qc.getQueryData<EngagementRequirementList>(key);
  if (before) {
    const set = new Set(ids);
    qc.setQueryData<EngagementRequirementList>(key, {
      ...before,
      items: before.items.map((r) => (set.has(r.id) ? { ...r, ...patch } : r)),
    });
  }
  return before;
}

function replaceCachedRow(qc: QueryClient, engagementId: string, row: EngagementRequirement) {
  const key = engagementRequirementKeys.list(engagementId);
  qc.setQueryData<EngagementRequirementList>(key, (prev) => {
    if (!prev) return prev;
    const exists = prev.items.some((r) => r.id === row.id);
    const items = exists
      ? prev.items.map((r) => (r.id === row.id ? row : r))
      : [...prev.items, row];
    return { items, total: items.length };
  });
}

/**
 * Inline edit of one row (text, criticality). Optimistic: the row changes
 * at once, is replaced by the server's row on success, and goes back to
 * what it was when the server refuses.
 */
export function useUpdateRequirementMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateRequirementInput) => updateRequirement(engagementId, input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: engagementRequirementKeys.list(engagementId) });
      const patch: RowPatch = {};
      // "Edited" is the wording or the criticality; the area and module are not.
      if (input.text !== undefined || input.criticality !== undefined) patch.is_edited = true;
      if (input.text !== undefined) patch.text = input.text;
      if (input.criticality !== undefined) patch.criticality = input.criticality;
      if (input.client_area !== undefined) patch.client_area = input.client_area?.trim() || null;
      return { before: patchCachedRows(qc, engagementId, [input.id], patch) };
    },
    onSuccess: (row) => replaceCachedRow(qc, engagementId, row),
    onError: (_err, _input, ctx) => {
      if (ctx?.before) {
        qc.setQueryData(engagementRequirementKeys.list(engagementId), ctx.before);
      }
    },
  });
}

export function useSetRequirementStatusMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; action: 'remove' | 'restore' }) =>
      setRequirementStatus(engagementId, input.id, input.action),
    onSuccess: (row) => replaceCachedRow(qc, engagementId, row),
  });
}

export function useCreateManualRequirementMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateManualRequirementInput) =>
      createManualRequirement(engagementId, input),
    onSuccess: (row) => {
      replaceCachedRow(qc, engagementId, row);
      void qc.invalidateQueries({ queryKey: engagementRequirementKeys.list(engagementId) });
    },
  });
}

export type BulkInput =
  | { kind: 'criticality'; ids: string[]; criticality: Criticality }
  | { kind: 'remove' | 'restore'; ids: string[] };

/** One bulk write: one request, one audited action on the server. */
export function useBulkRequirementsMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkInput) =>
      input.kind === 'criticality'
        ? bulkUpdateRequirements(engagementId, input.ids, input.criticality)
        : bulkSetRequirementStatus(engagementId, input.kind, input.ids),
    onSuccess: (result, input) => {
      patchCachedRows(
        qc,
        engagementId,
        result.ids,
        input.kind === 'criticality'
          ? { criticality: input.criticality, is_edited: true }
          : { status: input.kind === 'remove' ? 'removed' : 'active' },
      );
      void qc.invalidateQueries({ queryKey: engagementRequirementKeys.list(engagementId) });
    },
  });
}

export function useLoadLibraryMutation(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoadLibraryInput) => loadLibraryRequirements(engagementId, input),
    // Wait for the refetch so the caller lands on the filled table.
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: engagementRequirementKeys.list(engagementId) }),
  });
}
